import React from "react";
import styled from "styled-components";
import CodeDisplay from "../utils/CodeDisplay";
import Navigation from "../components/Header/nav";
import GitHubButton from "../utils/GitHubButton";
import TextShift from "../utils/TextShift";
import RevealOnScroll from "../utils/RevealOnScroll";

// Styled Components
const PageWrapper = styled.div`
  width: 60%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem;

  @media (max-width: 768px) {
    padding: 0.5rem;
    width: 90%;
  }
`;

const Section = styled.section`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h2`
  color: white;
  font-family: "Maitree", sans-serif;
  margin-bottom: 1rem;
`;

const SectionText = styled.p`
  color: #b5b5b5;
  font-family: "Maitree", sans-serif;
  line-height: 1.6;
  margin-bottom: 1rem;
`;

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 2rem;
`;

const ProjectTitle = styled.h1`
  color: white;
  font-family: "Maitree", sans-serif;
  margin: 0;
`;

const TitleWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 2rem 0;
  gap: 1rem;
`;

const AutoMail = () => {
  const gmailServiceCode = `
class GmailService:
    def __init__(self):
        self.creds = None
        self.SCOPES = ['https://www.googleapis.com/auth/gmail.modify']
        self.initialize_credentials()

    def initialize_credentials(self):
        if os.path.exists('token.pickle'):
            with open('token.pickle', 'rb') as token:
                self.creds = pickle.load(token)
        if not self.creds or not self.creds.valid:
            if self.creds and self.creds.expired and self.creds.refresh_token:
                self.creds.refresh(Request())
            else:
                flow = InstalledAppFlow.from_client_secrets_file(
                    'credentials/creds_google.json', self.SCOPES)
                self.creds = flow.run_local_server(port=0)
            with open('token.pickle', 'wb') as token:
                pickle.dump(self.creds, token)
  `;

  const emailHandlerCode = `
class EmailHandler:
    def __init__(self, service):
        self.service = service
        self.emails_received_dir = 'Emails_Received'
        os.makedirs(self.emails_received_dir, exist_ok=True)

    def get_label_ids(self, label_names):
        """Get Gmail label IDs for given label names."""
        try:
            results = self.service.users().labels().list(userId='me').execute()
            labels = results.get('labels', [])
            
            print("\\nAll available labels and their IDs:")
            for label in labels:
                print(f"{label['name']}: {label['id']}")
            
            label_ids = []
            for name in label_names:
                print(f"\\nFound label '{name}' with ID: ", end='')
                label_id = next((label['id'] for label in labels 
                               if label['name'] == name), None)
                print(label_id)
                if label_id:
                    label_ids.append(label_id)
            return label_ids
        except Exception as e:
            print(f"Error getting label IDs: {e}")
            return []
  `;

  const aiGeneratorCode = `
class AIGenerator:
    def __init__(self):
        self.client = OpenAI()
        self.assistant = self.client.beta.assistants.retrieve(
            assistant_id=os.getenv('OPENAI_ASSISTANT_ID')
        )

    def process_pending_emails(self):
        """Process all pending emails in the Emails_Received directory."""
        emails_dir = 'Emails_Received'
        responses_dir = 'Emails_To_Send'
        os.makedirs(responses_dir, exist_ok=True)

        # Find all unprocessed emails
        email_files = [f for f in os.listdir(emails_dir) 
                      if f.endswith('.json')]
        print(f"\\nFound {len(email_files)} unprocessed emails")

        for email_file in email_files:
            with open(os.path.join(emails_dir, email_file)) as f:
                email_content = json.load(f)
            
            print(f"\\nProcessing email from: {email_content['sender']}")
            print(f"Subject: {email_content['subject']}")

            # Create a thread for this email
            thread = self.client.beta.threads.create()
  `;

  const emailSenderCode = `
class EmailSender:
    def __init__(self):
        self.service = build('gmail', 'v1', credentials=GmailService().creds)

    def process_label_command(self, response_content, message_id):
        """Process special label commands in the response."""
        match = re.match(r'^\$([^$]+)\$(.*)', response_content, re.DOTALL)
        if match:
            label_name = match.group(1)
            actual_content = match.group(2).strip()
            
            if self.apply_label_to_message(message_id, label_name):
                self.remove_all_labels_except(message_id, label_name)
            
            return actual_content if actual_content else None

        return response_content

    def send_email(self, response_data):
        """Send an email response."""
        try:
            gmail_message = self.create_email_message(response_data)
            if gmail_message:
                message = self.service.users().messages().send(
                    userId='me',
                    body=gmail_message
                ).execute()
                print("Email sent successfully. Message ID:", message['id'])
                return True
            return False
        except Exception as e:
            print(f"Error sending email: {e}")
            return False
  `;

  const pipelineCode = `
def main():
    """Main pipeline for processing emails."""
    print("\\n=== Starting Email Processing Pipeline ===\\n")

    # Step 1: Fetch new emails
    print("Step 1: Fetching new emails...\\n")
    email_handler = EmailHandler(GmailService().build_service())
    email_handler.fetch_new_emails(['Beginner', 'Transfers'])

    # Step 2: Generate AI responses
    print("\\nStep 2: Generating AI responses...\\n")
    generator = AIGenerator()
    generator.process_pending_emails()

    # Step 3: Send responses
    print("\\nStep 3: Sending responses...\\n")
    sender = EmailSender()
    sender.process_pending_responses()

    # Step 4: Cleanup
    print("\\nStep 4: Cleaning up...")
    cleanup = CleanupUtil()
    cleanup.cleanup_processed_files('Emails_Received', 'Emails_To_Send')

    print("\\n=== Pipeline Complete ===\\n")
  `;

  return (
    <>
      <PageWrapper>
        <RevealOnScroll>
          <TitleWrapper>
            <TextShift 
              text="AutoMail" 
              fontFamily="moret-variable"
              fontSize="2rem"
            />
            <GitHubButton url="https://github.com/PinknMatter/Automail" />
          </TitleWrapper>
        </RevealOnScroll>
        <Section>
          <SectionTitle>Overview</SectionTitle>
          <SectionText>
            AutoMail is an intelligent email automation system that uses AI to process and respond 
            to emails automatically. It integrates with Gmail's API for email handling and OpenAI's 
            API for generating contextual responses. The system includes sophisticated label 
            management, allowing emails to be automatically categorized based on their content 
            and the AI's understanding of the request.
          </SectionText>
        </Section>

        {/* Gmail Service Section */}
        <Section>
          <SectionTitle>Gmail Service Integration</SectionTitle>
          <SectionText>
            The GmailService class handles authentication and credential management for Gmail API 
            access. It supports token persistence and automatic token refresh for seamless operation.
          </SectionText>
          <CodeDisplay code={gmailServiceCode} language="python" />
        </Section>

        {/* Email Handler Section */}
        <Section>
          <SectionTitle>Email Processing</SectionTitle>
          <SectionText>
            The EmailHandler class manages email fetching and label management. It can retrieve 
            emails based on specific labels and maintain a local cache of processed emails.
          </SectionText>
          <CodeDisplay code={emailHandlerCode} language="python" />
        </Section>

        {/* AI Integration Section */}
        <Section>
          <SectionTitle>AI-Powered Response Generation</SectionTitle>
          <SectionText>
            The AIGenerator class leverages OpenAI's API to generate contextually appropriate 
            responses to emails. It processes emails asynchronously and maintains conversation 
            context through OpenAI's thread system.
          </SectionText>
          <CodeDisplay code={aiGeneratorCode} language="python" />
        </Section>

        {/* Email Sender Section */}
        <Section>
          <SectionTitle>Intelligent Email Response System</SectionTitle>
          <SectionText>
            The EmailSender class handles response delivery and label management. It includes 
            special command processing that allows the AI to categorize emails by applying 
            specific labels based on the content and context of the email.
          </SectionText>
          <CodeDisplay code={emailSenderCode} language="python" />
        </Section>

        {/* Pipeline Section */}
        <Section>
          <SectionTitle>Automated Pipeline</SectionTitle>
          <SectionText>
            The main pipeline orchestrates the entire process, from fetching new emails to 
            sending AI-generated responses. It includes error handling and cleanup procedures 
            to ensure smooth operation.
          </SectionText>
          <CodeDisplay code={pipelineCode} language="python" />
        </Section>
      </PageWrapper>
    </>
  );
};

export default AutoMail;
